import { Dialog, DialogContent, DialogTitle } from '@/Components/Dialog/Dialog';
import CircularProgress from '@/Components/CircularProgress/CircularProgress';
import { type OrderForWorklist } from '@/Models/GenericWorklist/WorklistResult';
import { Procedure } from '@/Components/Procedure/Procedure';
import ReportPopupStore from '@/Pages/GenericWorklist/Components/ReportPopupStore';
import { DobIcon } from '@dedalusdiit/dob-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo } from 'react';
import { t } from 'i18next';
import Styles from './ReportPopup.module.scss';

interface ReportPopupProps {
    isOpen: boolean;
    onClose: () => void;
    selectedOrder: OrderForWorklist;
    selectedReportId?: number;
    selectedReportProcedureIds: number[];
}

export const ReportPopup = observer(({
    isOpen,
    onClose,
    selectedOrder,
    selectedReportId,
    selectedReportProcedureIds,
}: Readonly<ReportPopupProps>): React.ReactElement => {
    const store = useMemo(() => new ReportPopupStore(), []);
    useEffect(() => {
        store.setSelectionContext(selectedOrder, selectedReportId, selectedReportProcedureIds);
    }, [selectedOrder, selectedReportId, selectedReportProcedureIds, store]);

    const proceduresForSelectedReport = store.proceduresForSelectedReport;
    const {
        dictatedBy,
        dictatedDateTime,
        validatedBy,
        validatedDateTime,
        hasValidationData,
        selectedReportId: metadataReportId,
    } = store.reportMetadata;

    useEffect(() => {
        if (!isOpen || !metadataReportId) {
            store.reset();
            return;
        }

        const loadReportContent = async (): Promise<void> => {
            await store.loadReportContent(metadataReportId);
        };

        loadReportContent().catch((error) => {
            console.error('Failed to load report content from popup effect:', error);
        });
    }, [isOpen, metadataReportId, store]);

    const patientDisplayName = store.patientDisplayName;
    const patientDob = selectedOrder?.PatientInfo.PatientDOB ?? '';

    return (
        <Dialog open={isOpen} onClose={onClose} closeOnOutsideClick className={Styles.reportPopupDialog}>
            <DialogTitle onClose={onClose}>
                <span>
                    {patientDisplayName}
                    <DobIcon
                        name={selectedOrder.PatientInfo.Gender === 'M' ? 'gender-male' : 'gender-female'}
                        size="16px"
                        style={{ margin: '0 4px', verticalAlign: 'middle' }}
                    />
                    {patientDob}
                </span>
            </DialogTitle>
            <DialogContent>
                {store.isLoadingReportContent
                    ? (
                            <CircularProgress />
                        )
                    : (
                            <div className={Styles.popupBody}>
                                <div className={Styles.proceduresBlock}>
                                    <div className={Styles.proceduresSection}>
                                        {proceduresForSelectedReport.map((orderProcedure) => (
                                            <Procedure
                                                key={orderProcedure.OrderProcedureID}
                                                item={{
                                                    Code: orderProcedure.ProcedureInfo.Code,
                                                    Color: orderProcedure.ProcedureInfo.Color,
                                                    Description: orderProcedure.ProcedureInfo.Description,
                                                    Modality_Text: orderProcedure.ProcedureInfo.ModalityName ?? '-',
                                                    Room_Text: orderProcedure.HallInfo?.Name ?? null,
                                                    Radiologist_Text: null,
                                                    StatusIndicator: 0,
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>
                                <div className={Styles.reportContentSection}>
                                    <div className={Styles.reportContentBox}>
                                        {store.reportContent
                                            ? <div dangerouslySetInnerHTML={{ __html: store.reportContent }} />
                                            : null}
                                    </div>
                                </div>
                                <div className={Styles.metadataSection}>
                                    <div className={`${Styles.metadataRow} ${Styles.dictatedRow}`}>
                                        <span className={Styles.metadataContent}>
                                            <b>{t('DictatedBy')}</b>
                                            <span>{dictatedBy}</span>
                                            <span>{dictatedDateTime}</span>
                                        </span>
                                    </div>
                                    <div className={`${Styles.metadataRow} ${hasValidationData ? Styles.validatedRow : Styles.notValidatedRow}`}>
                                        {hasValidationData
                                            ? (
                                                    <span className={Styles.metadataContent}>
                                                        <b>{t('ValidatedBy')}</b>
                                                        <span>{validatedBy}</span>
                                                        <span>{validatedDateTime}</span>
                                                    </span>
                                                )
                                            : (
                                                    <span>{t('NotYetValidated')}</span>
                                                )}
                                    </div>
                                </div>
                            </div>
                        )}
            </DialogContent>
        </Dialog>
    );
});
